"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"

import type { LevelConfig, ViewportState, WaveConfig } from "@/types/game.types"
import { VIEWPORT_WIDTH, VIEWPORT_HEIGHT } from "@/data/game-settings"
import { enemyTypes } from "@/data/enemy-types"
import { gameNow } from "@/utils/game-clock"
import { ENEMY_SPRITESHEET, ENEMY_SPRITE_INDEX } from "@/components/game-ui/enemy-sprite-index"

type SpawnSide = "top" | "left" | "right" | "bottom"

const EARLY_CALL_COOLDOWN_MS = 5000 // 5 seconds before early call is allowed
const EARLY_CALL_WINDOW_MS = 20000 // 20 seconds total window for early call bonus

const SPRITE_SIZE = 64


// Get the strongest enemy type from a wave (highest level, then unique > elite > regular)
function getStrongestEnemyFromWave(wave: WaveConfig): { type: string; variant: "regular" | "elite" | "unique" } {
  let strongest: { type: string; variant: "regular" | "elite" | "unique"; level: number } | null = null

  for (const enemyGroup of wave.enemies) {
    // Parse type:variant format (e.g., "rat:elite" or just "rat")
    const [baseType, variant = "regular"] = enemyGroup.type.split(":") as [string, "regular" | "elite" | "unique" | undefined]
    const actualVariant = (variant || "regular") as "regular" | "elite" | "unique"

    const enemyConfig = enemyTypes[baseType]
    if (!enemyConfig) continue

    const level = enemyConfig.level || 1
    const variantPriority = actualVariant === "unique" ? 3 : actualVariant === "elite" ? 2 : 1

    if (!strongest || level > strongest.level || (level === strongest.level && variantPriority > (strongest.variant === "unique" ? 3 : strongest.variant === "elite" ? 2 : 1))) {
      strongest = { type: baseType, variant: actualVariant, level }
    }
  }

  return strongest || { type: "rat", variant: "regular" }
}

// Merge a wave's spawn groups into displayable [enemy x count] rows,
// strongest first, so the player can judge an early call
interface WaveCompositionRow {
  type: string
  variant: "regular" | "elite" | "unique"
  count: number
  spriteIndex: number
}

function getWaveComposition(wave: WaveConfig): WaveCompositionRow[] {
  const merged = new Map<string, WaveCompositionRow>()
  for (const group of wave.enemies) {
    const [baseType, variant = "regular"] = group.type.split(":") as [string, "regular" | "elite" | "unique" | undefined]
    const actualVariant = (variant || "regular") as "regular" | "elite" | "unique"
    const key = `${baseType}:${actualVariant}`
    const existing = merged.get(key)
    if (existing) {
      existing.count += group.count
    } else {
      merged.set(key, {
        type: baseType,
        variant: actualVariant,
        count: group.count,
        spriteIndex: ENEMY_SPRITE_INDEX[baseType] ?? 0,
      })
    }
  }
  const variantRank = { unique: 3, elite: 2, regular: 1 } as const
  return [...merged.values()].sort((a, b) => variantRank[b.variant] - variantRank[a.variant])
}

// Variant ring colours matching the new-enemy badge (gold/purple/red)
const VARIANT_RING: Record<"regular" | "elite" | "unique", string> = {
  regular: "rgba(255,255,255,0.25)",
  elite: "#a855f7",
  unique: "#dc2626",
}

interface NextWaveButtonProps {
  level: LevelConfig
  viewport: ViewportState
  onStartWave: () => void
  onCallEarly?: (awardBonus: boolean) => void
  show: boolean
  waveStartTime?: number | null // Timestamp when current wave started (for early call)
  isLastWave?: boolean // Whether this is the last wave
  isFirstWave?: boolean // Whether this is the first wave (wave 0)
  isMobile?: boolean
  isDesktop?: boolean
  pillarWidth?: number
  showTutorial?: boolean // Show "Call first wave" tutorial bubble
  onTutorialDismiss?: () => void // Called when tutorial is dismissed (on first click)
  currentWaveIndex?: number // Current wave index to determine next wave
}

function getSpawnSide(leftPercent: number, topPercent: number): SpawnSide {
  // Check if spawn point is outside viewport and determine which edge it's closest to
  const distanceFromLeft = leftPercent
  const distanceFromRight = 100 - leftPercent
  const distanceFromTop = topPercent
  const distanceFromBottom = 100 - topPercent

  // If spawn is within viewport, default to top
  if (leftPercent >= 0 && leftPercent <= 100 && topPercent >= 0 && topPercent <= 100) {
    // Find closest edge
    const minDistance = Math.min(distanceFromLeft, distanceFromRight, distanceFromTop, distanceFromBottom)
    if (minDistance === distanceFromTop) return "top"
    if (minDistance === distanceFromBottom) return "bottom"
    if (minDistance === distanceFromLeft) return "left"
    return "right"
  }

  // Outside viewport - determine primary direction
  if (topPercent < 0) return "top"
  if (topPercent > 100) return "bottom"
  if (leftPercent < 0) return "left"
  if (leftPercent > 100) return "right"

  return "top"
}

function getArrowStyles(side: SpawnSide): {
  arrowRotation: string
  arrowPosition: React.CSSProperties
  tooltipPosition: React.CSSProperties
  containerFlexDirection: "row" | "row-reverse" | "column" | "column-reverse"
  tooltipPointerSide: "top" | "bottom" | "left" | "right"
} {
  switch (side) {
    case "top":
      return {
        arrowRotation: "rotate(0deg)",
        arrowPosition: { marginBottom: "-2px" },
        tooltipPosition: { marginTop: "12px" },
        containerFlexDirection: "column",
        tooltipPointerSide: "top",
      }
    case "bottom":
      return {
        arrowRotation: "rotate(180deg)",
        arrowPosition: { marginTop: "-2px" },
        tooltipPosition: { marginBottom: "12px" },
        containerFlexDirection: "column-reverse",
        tooltipPointerSide: "bottom",
      }
    case "left":
      return {
        arrowRotation: "rotate(-90deg)",
        arrowPosition: { marginRight: "-2px" },
        tooltipPosition: { marginLeft: "12px" },
        containerFlexDirection: "row",
        tooltipPointerSide: "left",
      }
    case "right":
      return {
        arrowRotation: "rotate(90deg)",
        arrowPosition: { marginLeft: "-2px" },
        tooltipPosition: { marginRight: "12px" },
        containerFlexDirection: "row-reverse",
        tooltipPointerSide: "right",
      }
  }
}

function TooltipPointer({ side }: { side: "top" | "bottom" | "left" | "right" }) {
  const baseStyles: React.CSSProperties = {
    position: "absolute",
    width: "0",
    height: "0",
  }

  const innerBaseStyles: React.CSSProperties = {
    position: "absolute",
    width: "0",
    height: "0",
  }

  switch (side) {
    case "top":
      return (
        <>
          <div
            style={{
              ...baseStyles,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderBottom: "12px solid var(--game-border)",
              bottom: "100%",
              left: "50%",
              marginLeft: "-10px",
            }}
          />
          <div
            style={{
              ...innerBaseStyles,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderBottom: "10px solid var(--game-surface-overlay)",
              bottom: "100%",
              left: "50%",
              marginLeft: "-8px",
            }}
          />
        </>
      )
    case "bottom":
      return (
        <>
          <div
            style={{
              ...baseStyles,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "12px solid var(--game-border)",
              top: "100%",
              left: "50%",
              marginLeft: "-10px",
            }}
          />
          <div
            style={{
              ...innerBaseStyles,
              borderLeft: "8px solid transparent",
              borderRight: "8px solid transparent",
              borderTop: "10px solid var(--game-surface-overlay)",
              top: "100%",
              left: "50%",
              marginLeft: "-8px",
            }}
          />
        </>
      )
    case "left":
      return (
        <>
          <div
            style={{
              ...baseStyles,
              borderTop: "10px solid transparent",
              borderBottom: "10px solid transparent",
              borderRight: "12px solid var(--game-border)",
              right: "100%",
              top: "50%",
              marginTop: "-10px",
            }}
          />
          <div
            style={{
              ...innerBaseStyles,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderRight: "10px solid var(--game-surface-overlay)",
              right: "100%",
              top: "50%",
              marginTop: "-8px",
            }}
          />
        </>
      )
    case "right":
      return (
        <>
          <div
            style={{
              ...baseStyles,
              borderTop: "10px solid transparent",
              borderBottom: "10px solid transparent",
              borderLeft: "12px solid var(--game-border)",
              left: "100%",
              top: "50%",
              marginTop: "-10px",
            }}
          />
          <div
            style={{
              ...innerBaseStyles,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderLeft: "10px solid var(--game-surface-overlay)",
              left: "100%",
              top: "50%",
              marginTop: "-8px",
            }}
          />
        </>
      )
  }
}

export const NextWaveButton = ({
  level,
  viewport,
  onStartWave,
  onCallEarly,
  show,
  waveStartTime,
  isLastWave = false,
  isFirstWave = false,
  isMobile = false,
  isDesktop = false,
  pillarWidth = 0,
  showTutorial = false,
  onTutorialDismiss,
  currentWaveIndex = 0,
}: NextWaveButtonProps) => {
  // Track cooldown progress for early wave call
  const [cooldownProgress, setCooldownProgress] = useState(0)
  const [earlyCallSecondsRemaining, setEarlyCallSecondsRemaining] = useState(0)
  const hasAutoStartedRef = useRef(false)
  const isWaveActive = waveStartTime !== null && waveStartTime !== undefined
  const canCallEarly = isWaveActive && !isLastWave && cooldownProgress >= 100

  // Check if we're in the early call bonus window (5s-20s after wave start)
  const isInEarlyCallWindow = isWaveActive && cooldownProgress >= 100 && earlyCallSecondsRemaining > 0

  // Reset auto-start flag when wave changes
  useEffect(() => {
    hasAutoStartedRef.current = false
  }, [waveStartTime])

  // Auto-start wave when early call window expires (timer reaches 0)
  useEffect(() => {
    if (!isWaveActive || isLastWave || !canCallEarly) return
    if (hasAutoStartedRef.current) return

    // When timer just hit 0, auto-start the next wave with no bonus
    if (earlyCallSecondsRemaining === 0 && onCallEarly) {
      hasAutoStartedRef.current = true
      // Play new wave sound
      const audio = new Audio("/sounds/events/newwave.mp3")
      audio.volume = 0.5
      audio.play().catch(() => {})
      // Auto-start with no bonus
      onCallEarly(false)
    }
  }, [earlyCallSecondsRemaining, isWaveActive, isLastWave, canCallEarly, onCallEarly])

  // Update cooldown progress and early call window timer during active wave
  useEffect(() => {
    if (!isWaveActive || isLastWave) {
      setCooldownProgress(0)
      setEarlyCallSecondsRemaining(0)
      return
    }

    const updateProgress = () => {
      const elapsed = gameNow() - waveStartTime
      const progress = Math.min((elapsed / EARLY_CALL_COOLDOWN_MS) * 100, 100)
      setCooldownProgress(progress)

      // Calculate remaining seconds in early call window
      const windowRemaining = Math.max(0, EARLY_CALL_WINDOW_MS - elapsed)
      setEarlyCallSecondsRemaining(Math.ceil(windowRemaining / 1000))
    }

    updateProgress()
    const interval = setInterval(updateProgress, 50)
    return () => clearInterval(interval)
  }, [isWaveActive, waveStartTime, isLastWave])

  // Show button during wave (for early call) OR when wave is complete
  const shouldShow = show || (isWaveActive && !isLastWave)
  if (!shouldShow || !level.path || level.path.length < 1) {
    return null
  }

  // Determine which wave to show enemy for
  // During active wave, show current wave's strongest enemy
  // When waiting for next wave, show next wave's strongest enemy
  const waveIndexToShow = isWaveActive ? currentWaveIndex : currentWaveIndex
  const waveToShow = level.waves[waveIndexToShow]
  const strongestEnemy = waveToShow ? getStrongestEnemyFromWave(waveToShow) : { type: "rat", variant: "regular" as const }
  const spriteIndex = ENEMY_SPRITE_INDEX[strongestEnemy.type] ?? 0

  const spawnPoint = level.path[0]
  const zoom = viewport.zoom

  // Convert world coordinates to screen coordinates
  const screenX = (spawnPoint.x - viewport.x) * zoom
  const screenY = (spawnPoint.y - viewport.y) * zoom

  // Convert to percentage of viewport (use actual viewport dimensions, not constants)
  const viewportWidth = viewport.width || VIEWPORT_WIDTH
  const viewportHeight = viewport.height || VIEWPORT_HEIGHT
  const leftPercent = (screenX / viewportWidth) * 100
  const topPercent = (screenY / viewportHeight) * 100

  // Check if the button would be fully visible (not partially cut off)
  // The bubble button is 56px + tooltip, we need at least ~100px from each edge to show fully
  // Using stricter bounds to ensure the bubble with tooltip is never partially visible
  const isFullyVisible = leftPercent > 5 && leftPercent < 95 && topPercent > 5 && topPercent < 85

  const isDocked = !isFullyVisible

  const spawnSide = getSpawnSide(leftPercent, topPercent)
  const { arrowRotation, arrowPosition, tooltipPosition, containerFlexDirection, tooltipPointerSide } =
    getArrowStyles(spawnSide)

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Dismiss tutorial on first click
    if (onTutorialDismiss) {
      onTutorialDismiss()
    }

    if (isWaveActive) {
      // During wave - call early if cooldown complete
      if (canCallEarly && onCallEarly) {
        console.log("[v0] NextWaveButton: Calling wave early, bonus:", isInEarlyCallWindow)
        // Play new wave sound
        const audio = new Audio("/sounds/events/newwave.mp3")
        audio.volume = 0.5
        audio.play().catch(() => {})
        onCallEarly(isInEarlyCallWindow)
      }
    } else {
      // Wave complete - normal next wave
      console.log("[v0] NextWaveButton clicked")
      onStartWave()
    }
  }

  if (isDocked) {
    const isDisabled = isWaveActive && !canCallEarly
    const isOnCooldown = isWaveActive && cooldownProgress < 100
    // On desktop, position inside center column (pillarWidth from right edge)
    const rightOffset = isDesktop ? pillarWidth + 16 : 16
    // Button size: 48px * 1.5 = 72px
    const buttonSize = 72
    return (
      <div
        id="next-wave-button-docked"
        className="fixed pointer-events-auto"
        style={{
          right: `${rightOffset}px`,
          top: "16px",
          zIndex: isMobile ? 35 : 100,
        }}
      >
        <button
          id="start-wave-button-docked"
          onClick={handleClick}
          onTouchEnd={handleClick}
          disabled={isDisabled}
          className={`relative transition-all duration-300 pointer-events-auto ${
            isDisabled ? "cursor-not-allowed" : "scale-100 hover:scale-105 cursor-pointer"
          }`}
          style={{
            width: `${buttonSize}px`,
            height: `${buttonSize}px`,
            touchAction: "manipulation",
          }}
        >
          {/* Outer border - square with rounded corners */}
          <div
            className="absolute inset-0 rounded-lg border-3"
            style={{
              borderColor: isOnCooldown ? "#6b7280" : "var(--game-wave-secondary)",
              backgroundColor: isOnCooldown ? "#374151" : "var(--game-wave-primary)",
              boxShadow: isOnCooldown
                ? "0 0 0 1px #374151, 0 2px 6px rgba(0, 0, 0, 0.4)"
                : "0 0 0 1px var(--game-wave-primary), 0 2px 6px rgba(0, 0, 0, 0.4)",
              borderWidth: "3px",
            }}
          />

          {/* Ping animation on border when ready */}
          {!isOnCooldown && (
            <div
              className="absolute inset-0 rounded-lg animate-ping pointer-events-none"
              style={{
                borderColor: "var(--game-wave-secondary)",
                borderWidth: "3px",
                borderStyle: "solid",
                opacity: 0.75,
              }}
            />
          )}

          {/* Inner area with enemy sprite */}
          <div
            className="absolute inset-1 rounded-md bg-game-surface flex items-center justify-center overflow-hidden"
            style={{ boxShadow: "inset 0 1px 4px rgba(0, 0, 0, 0.6)" }}
          >
            {/* Enemy sprite from spritesheet */}
            <div
              style={{
                width: buttonSize - 12,
                height: buttonSize - 12,
                backgroundImage: `url(${ENEMY_SPRITESHEET})`,
                backgroundPosition: `-${spriteIndex * SPRITE_SIZE * ((buttonSize - 12) / SPRITE_SIZE)}px 0`,
                backgroundSize: `${16 * (buttonSize - 12)}px ${buttonSize - 12}px`,
                imageRendering: "pixelated",
                filter: isOnCooldown ? "grayscale(100%)" : "none",
                opacity: isDisabled ? 0.7 : 1,
              }}
            />
          </div>

          {/* Cooldown progress overlay */}
          {isWaveActive && cooldownProgress < 100 && (
            <div
              className="absolute inset-0 rounded-lg pointer-events-none"
              style={{
                background: `linear-gradient(to top, transparent ${cooldownProgress}%, rgba(0, 0, 0, 0.5) ${cooldownProgress}%)`,
              }}
            />
          )}

          {/* Countdown timer when in early call window */}
          {isInEarlyCallWindow && (
            <div
              className="absolute bottom-1 right-1 bg-black/80 rounded px-1 text-white font-bold pointer-events-none"
              style={{ fontSize: 10 }}
            >
              {earlyCallSecondsRemaining}s
            </div>
          )}

          {/* Tutorial bubble - Call first wave */}
          {showTutorial && (
            <div
              className="absolute pointer-events-none z-40"
              style={{
                right: buttonSize + 8,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <div className="relative bg-black border border-white/50 rounded-lg px-3 py-2 text-white text-center whitespace-nowrap text-xs">
                <div>Call first wave</div>
                {/* Diamond pointer pointing RIGHT */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 -right-2 w-3 h-3 bg-black border-r border-b border-white/50 -rotate-45"
                />
              </div>
            </div>
          )}
        </button>

        {/* Upcoming wave composition - shown while deciding to call it */}
        {!isWaveActive && waveToShow && (
          <div className="mt-2 flex flex-col items-end gap-1 pointer-events-none">
            {getWaveComposition(waveToShow).map((row) => (
              <div
                key={`${row.type}:${row.variant}`}
                className="flex items-center gap-1.5 bg-black/60 rounded-lg pl-1 pr-2 py-0.5"
              >
                <div
                  className="rounded-md overflow-hidden shrink-0"
                  style={{
                    width: 22,
                    height: 22,
                    border: `2px solid ${VARIANT_RING[row.variant]}`,
                    backgroundImage: `url(${ENEMY_SPRITESHEET})`,
                    backgroundPosition: `-${row.spriteIndex * 18}px 0`,
                    backgroundSize: `${16 * 18}px 18px`,
                    imageRendering: "pixelated",
                  }}
                  title={`${row.type}${row.variant !== "regular" ? ` (${row.variant})` : ""}`}
                />
                <span className="text-white text-xs font-bold">×{row.count}</span>
                {row.variant === "unique" && (
                  <span className="text-red-400 text-[10px] font-bold uppercase">Boss</span>
                )}
                {row.variant === "elite" && (
                  <span className="text-purple-300 text-[10px] font-bold uppercase">Elite</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Interest bonus message when in early call window (not shown for first wave) */}
        {isInEarlyCallWindow && !isFirstWave && (
          <div
            className="absolute pointer-events-none z-40"
            style={{
              right: buttonSize + 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <div className="relative bg-black border border-white/50 rounded-lg px-3 py-2 pr-4 text-white text-center whitespace-nowrap text-xs flex items-center gap-0.5">
              <span>Call early</span>
              <span>+</span>
              <img
                src="/images/icon-coins.png"
                alt="coins"
                className="inline-block"
                style={{ width: 12, height: 12, imageRendering: "pixelated" }}
              />
              {/* Diamond pointer pointing RIGHT */}
              <div
                className="absolute top-1/2 -translate-y-1/2 -right-2 w-3 h-3 bg-black border-r border-b border-white/50 -rotate-45"
              />
            </div>
          </div>
        )}
      </div>
    )
  }

  // Arrow is 20px tall
  const arrowHeight = 20

  const getTransform = () => {
    switch (spawnSide) {
      case "top":
        return `translate(-50%, 0) scale(${zoom})`
      case "bottom":
        return `translate(-50%, -100%) scale(${zoom})`
      case "left":
        return `translate(0, -50%) scale(${zoom})`
      case "right":
        return `translate(-100%, -50%) scale(${zoom})`
    }
  }

  const getTransformOrigin = () => {
    switch (spawnSide) {
      case "top":
        return "top center"
      case "bottom":
        return "bottom center"
      case "left":
        return "center left"
      case "right":
        return "center right"
    }
  }

  return (
    <div
      id="next-wave-button-container"
      className="absolute pointer-events-auto"
      style={{
        left: `${leftPercent}%`,
        top: `${topPercent}%`,
        transform: getTransform(),
        transformOrigin: getTransformOrigin(),
        zIndex: isMobile ? 35 : 100,
      }}
    >
      <div className="relative flex items-center justify-center" style={{ flexDirection: containerFlexDirection }}>
        {/* Arrow pointing to spawn point */}
        <div
          id="wave-button-arrow"
          style={{
            width: "0",
            height: "0",
            borderLeft: "14px solid transparent",
            borderRight: "14px solid transparent",
            borderBottom: `${arrowHeight}px solid var(--game-wave-primary)`,
            filter: "drop-shadow(0 -2px 4px rgba(0, 0, 0, 0.3))",
            transform: arrowRotation,
            ...arrowPosition,
          }}
        />

        {/* Square button with enemy sprite */}
        {(() => {
          const isDisabled = isWaveActive && !canCallEarly
          const isOnCooldown = isWaveActive && cooldownProgress < 100
          // Button size: 56px * 1.5 = 84px
          const buttonSize = 84
          return (
            <button
              id="start-wave-button"
              onClick={handleClick}
              onTouchEnd={handleClick}
              disabled={isDisabled}
              className={`relative transition-all duration-300 pointer-events-auto ${
                isDisabled ? "cursor-not-allowed" : "scale-100 hover:scale-105 cursor-pointer"
              }`}
              style={{
                width: `${buttonSize}px`,
                height: `${buttonSize}px`,
                touchAction: "manipulation",
              }}
            >
              {/* Outer border - square with rounded corners */}
              <div
                className="absolute inset-0 rounded-lg"
                style={{
                  borderColor: isOnCooldown ? "#6b7280" : "var(--game-wave-secondary)",
                  backgroundColor: isOnCooldown ? "#374151" : "var(--game-wave-primary)",
                  boxShadow: isOnCooldown
                    ? "0 0 0 1px #374151, 0 2px 6px rgba(0, 0, 0, 0.4)"
                    : "0 0 0 1px var(--game-wave-primary), 0 2px 6px rgba(0, 0, 0, 0.4)",
                  borderWidth: "3px",
                  borderStyle: "solid",
                }}
              />

              {/* Ping animation on border when ready */}
              {!isOnCooldown && (
                <div
                  className="absolute inset-0 rounded-lg animate-ping pointer-events-none"
                  style={{
                    borderColor: "var(--game-wave-secondary)",
                    borderWidth: "3px",
                    borderStyle: "solid",
                    opacity: 0.75,
                  }}
                />
              )}

              {/* Inner area with enemy sprite */}
              <div
                className="absolute inset-1 rounded-md bg-game-surface flex items-center justify-center overflow-hidden"
                style={{ boxShadow: "inset 0 1px 4px rgba(0, 0, 0, 0.6)" }}
              >
                {/* Enemy sprite from spritesheet */}
                <div
                  style={{
                    width: buttonSize - 12,
                    height: buttonSize - 12,
                    backgroundImage: `url(${ENEMY_SPRITESHEET})`,
                    backgroundPosition: `-${spriteIndex * SPRITE_SIZE * ((buttonSize - 12) / SPRITE_SIZE)}px 0`,
                    backgroundSize: `${16 * (buttonSize - 12)}px ${buttonSize - 12}px`,
                    imageRendering: "pixelated",
                    filter: isOnCooldown ? "grayscale(100%)" : "none",
                    opacity: isDisabled ? 0.7 : 1,
                  }}
                />
              </div>

              {/* Cooldown progress overlay */}
              {isWaveActive && cooldownProgress < 100 && (
                <div
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  style={{
                    background: `linear-gradient(to top, transparent ${cooldownProgress}%, rgba(0, 0, 0, 0.5) ${cooldownProgress}%)`,
                  }}
                />
              )}

              {/* Countdown timer when in early call window */}
              {isInEarlyCallWindow && (
                <div
                  className="absolute bottom-1 right-1 bg-black/80 rounded px-1 text-white font-bold pointer-events-none"
                  style={{ fontSize: 10 }}
                >
                  {earlyCallSecondsRemaining}s
                </div>
              )}
            </button>
          )
        })()}

        {/* Text tooltip - position changes based on spawn side */}
        <div className="pointer-events-none" style={tooltipPosition}>
          <div
            className="relative bg-game-surface-overlay rounded-lg px-4 py-3 border-2 border-game-border shadow-lg"
            style={{ minWidth: "180px" }}
          >
            <TooltipPointer side={tooltipPointerSide} />

            <div className="text-center">
              <div className="text-sm font-bold text-foreground mb-1">
                {isWaveActive ? (canCallEarly ? "CALL NEXT WAVE" : "WAVE IN PROGRESS") : "START BATTLE!"}
              </div>
              <div className="text-[10px] font-semibold text-muted-foreground flex items-center justify-center gap-1">
                {isWaveActive
                  ? canCallEarly
                    ? isFirstWave
                      ? "TAP TO START BATTLE"
                      : isInEarlyCallWindow
                        ? <>TAP FOR +<img src="/images/icon-coins.png" alt="coins" style={{ width: 10, height: 10, imageRendering: "pixelated", display: "inline" }} /></>
                        : "TAP TO CALL NEXT WAVE"
                    : `WAIT ${Math.ceil((EARLY_CALL_COOLDOWN_MS - (gameNow() - (waveStartTime || 0))) / 1000)}s`
                  : "TAP TO CALL WAVE"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
